import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, BookOpen, CalendarDays, Bot, LogOut, ShieldCheck, GitCompareArrows, Wrench, CreditCard } from "lucide-react";
import { useAuth } from "../lib/auth";

const links = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "nav-dashboard" },
  { to: "/app/properties", icon: Building2, label: "Properties", testid: "nav-properties" },
  { to: "/app/operations", icon: Wrench, label: "Operations", testid: "nav-operations" },
  { to: "/app/library", icon: BookOpen, label: "Rule Library", testid: "nav-library" },
  { to: "/app/compare", icon: GitCompareArrows, label: "Compare Cities", testid: "nav-compare" },
  { to: "/app/calendar", icon: CalendarDays, label: "Calendar", testid: "nav-calendar" },
  { to: "/app/assistant", icon: Bot, label: "AI Assistant", testid: "nav-assistant" },
  { to: "/app/billing", icon: CreditCard, label: "Billing", testid: "nav-billing" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 grain">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="hidden md:flex w-64 shrink-0 flex-col bg-slate-900 text-slate-100 sticky top-0 h-screen">
          <div className="px-6 py-8 border-b border-slate-800">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-sky-500 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-display font-bold text-lg leading-tight">HavenGuard</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">STR Compliance OS</div>
              </div>
            </div>
          </div>
          <nav className="flex-1 px-3 py-6 space-y-1">
            {links.map(({ to, icon: Icon, label, end, testid }) => (
              <NavLink key={to} to={to} end={end} data-testid={testid}
                className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition ${isActive ? "bg-sky-500 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}>
                <Icon className="w-4 h-4" />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 border-t border-slate-800">
            <div className="px-2 py-2 text-xs">
              <div className="text-slate-400">Signed in as</div>
              <div className="font-medium text-slate-100 truncate" data-testid="user-email">{user?.email}</div>
            </div>
            <button onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn"
              className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </aside>

        {/* Mobile top bar */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-slate-900 text-slate-100 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-sky-400" />
            <span className="font-display font-bold">HavenGuard</span>
          </div>
          <button onClick={() => { logout(); nav("/login"); }} data-testid="mobile-logout-btn" className="text-xs text-slate-300">Sign out</button>
        </div>

        {/* Main */}
        <main className="flex-1 min-w-0 pt-14 md:pt-0 pb-24 md:pb-0">
          <div className="p-4 sm:p-6 lg:p-10 max-w-[1400px] mx-auto">
            <Outlet />
          </div>
        </main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 flex justify-around py-1.5">
          {links.map(({ to, icon: Icon, label, end, testid }) => (
            <NavLink key={to} to={to} end={end} data-testid={`m-${testid}`}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1.5 text-[10px] ${isActive ? "text-sky-600" : "text-slate-500"}`}>
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
